/**
 * Tenant access utilities.
 * Diambra tenant: only users with @diambra.ai OR @ungaii.co email OR role === 'admin' (superadmin) can access.
 * Smowl tenant: @smowltech.com maps to tenant smowl via getTenantFromEmail (login allowed).
 */

const DIAMBRA_EMAIL_DOMAINS = ['diambra.ai', 'ungaii.co'];

export function isDiambraEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const normalizedEmail = email.trim().toLowerCase();
  return DIAMBRA_EMAIL_DOMAINS.some(domain => normalizedEmail.endsWith(`@${domain}`));
}

export function isSmowlEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@smowltech.com');
}

export function isWaygroundEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const n = email.trim().toLowerCase();
  return n.endsWith('@quizizz.com') || n.endsWith('@wayground.io') || n.endsWith('@wayground.com');
}

export function isPlataformaaEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const n = email.trim().toLowerCase();
  return n === 'dimahega@gmail.com' || n.endsWith('@plataformaa.com');
}

export function isEvasysEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@evasys.de');
}

export function isCalmedicaEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@calmedica.com');
}

export function isLearnwiseEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@learnwise.ai');
}

export function isDemarqueEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@demarque.com');
}

export function isBibliothecaEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@bibliotheca.com');
}

/** [2026-10-28] Niryo (educational robot arms). Access restricted to @niryo.com (Tiago). */
export function isNiryoEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@niryo.com');
}

/** Domains blocked from signing in or using the app (same UX as admin block message).
 *  [2026-10-29] @unit4.com temporarily blocked (Tiago). Revert to `return false` to lift. */
export function isPlatformAccessBlockedEmail(email) {
  return isUnit4Email(email);
}

export function isFunidataEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@funidata.fi');
}

export function isAkariEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@akarisoftware.com');
}

export function isAlmentorEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@almentor.net');
}

export function isGravytyEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@gravyty.com');
}

export function isInfoneticaEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@infonetica.net');
}

export function isCayuseEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@cayuse.com');
}

export function isUplannerEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@uplanner.com');
}

export function isEdusignEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@edusign.fr');
}

export function isLiaisonEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@liaisonedu.com');
}

export function isRumiEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@rumiessay.com');
}

export function isStudentcrmEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@student-crm.co.uk');
}

export function isUnit4Email(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@unit4.com');
}

export function isBlackboardEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@blackboard.com');
}

export function isConstructorEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@constructor.tech');
}

export function isEfektaEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@efekta.com');
}

export function isJpikEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@jpik.com');
}

/** [2026-10-29] Blackbullion. Its staff sign in on @blackbullion.com (Tiago, 2026-10-29). */
export function isBlackbullionEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@blackbullion.com');
}

/** [2026-10-31] Wooclap. Its staff sign in on @wooclap.com (Tiago, 2026-10-31). */
export function isWooclapEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@wooclap.com');
}

/** [2026-11-03] Positivo Tecnologia. Its staff sign in on @positivo.com.br (Tiago, 2026-11-03). */
export function isPositivoEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@positivo.com.br');
}

/** [2026-11-02] Poll Everywhere. Its staff sign in on @polleverywhere.com (Tiago, 2026-11-02). */
export function isPolleverywhereEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@polleverywhere.com');
}

/** TestWe. Its users sign in on @testwe.eu (Tiago, 2026-10-29; was @testwe.edu). */
export function isTestweEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@testwe.eu');
}

/** [2026-10-19] DreamApply (international admissions CRM). Its users sign in on @dreamapply.com. */
export function isDreamapplyEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@dreamapply.com');
}

/** [2026-10-21] 4Science (DSpace, DSpace-CRIS, digital preservation). Staff sign in on @4science.com. */
export function is4scienceEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@4science.com');
}

export function isFeedbackfruitsEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@feedbackfruits.com');
}

export function isFullfabricEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@fullfabric.com');
}

export function isSurpassEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@surpass.com');
}

const SEBA_EMAIL_DOMAINS = ['turktelekom.com.tr', 'argela.com.tr', 'netsia.com'];

export function isSebaEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const normalizedEmail = email.trim().toLowerCase();
  return SEBA_EMAIL_DOMAINS.some((domain) => normalizedEmail.endsWith(`@${domain}`));
}

/** Resolve tenant from email domain for auth/signup bootstrap flows. */
export function getTenantFromEmail(email, defaultTenant = 'instructure') {
  if (!email || typeof email !== 'string') return defaultTenant;
  const domain = email.trim().toLowerCase().split('@')[1] || '';
  if (domain === 'clairelabs.ai' || domain === 'neogtm.com') return 'clairelabs';
  // Corporate employees use V2 only. Legacy `demarque` is not a workspace for them.
  if (domain === 'demarque.com') return 'demarque_V2';
  if (domain === 'bibliotheca.com') return 'bibliotheca';
  if (domain === 'niryo.com') return 'niryo';
  if (domain === 'smowltech.com') return 'smowl';
  // Corporate employees use V2 only. Legacy `surpass` is not a workspace for them.
  if (domain === 'surpass.com') return 'surpass_V2';
  if (domain === 'diambra.ai' || domain === 'ungaii.co') return 'diambra';
  if (domain === 'edqwest.com') return 'edqwest';
  if (domain === 'calmedica.com') return 'calmedica';
  if (domain === 'fariaedu.com') return 'faria';
  if (domain === 'prowise.com') return 'prowise';
  if (domain === 'skillwell.com') return 'instructure';
  if (domain === 'skillwell.co' || domain === 'etu.co') return 'skillwell';
  if (domain === 'kairadigital.com') return 'kairadigital';
  if (domain === 'wayground.io' || domain === 'wayground.com') return 'wayground';
  if (domain === 'plataformaa.com') return 'plataformaa';
  // [2026-10-21] Corporate employees use V2 only — mirrors api/src/services/tenantResolver.ts,
  // which has mapped both evasys.de and evasys.com to `evasys_V2` since 2026-09-01. This line
  // returned the LEGACY `evasys` and contradicted the server: legacy has no entitlement and no
  // market profile, so a user it routed there got a Market page that cannot work, and the BE
  // then healed them to evasys_V2 anyway. Same shape as feedbackfruits.com and fullfabric.com
  // below. No email domain routes to legacy `evasys` any more.
  if (domain === 'evasys.de' || domain === 'evasys.com') return 'evasys_V2';
  if (domain === 'learnwise.ai') return 'learnwise';
  if (domain === 'funidata.fi') return 'funidata';
  if (domain === 'akarisoftware.com') return 'akari';
  if (domain === 'almentor.net') return 'almentor';
  if (domain === 'gravyty.com') return 'gravyty';
  if (domain === 'infonetica.net') return 'infonetica';
  if (domain === 'cayuse.com') return 'cayuse';
  if (domain === 'uplanner.com') return 'uplanner';
  if (domain === 'edusign.fr') return 'edusign';
  if (domain === 'liaisonedu.com') return 'liaison';
  if (domain === 'vretta.com') return 'vretta';
  // Corporate employees use V2 only. Legacy `fullfabric` is not a workspace for them.
  if (domain === 'fullfabric.com') return 'fullfabric_V2';
  if (domain === 'rumiessay.com') return 'rumi';
  if (domain === 'student-crm.co.uk') return 'studentcrm';
  if (domain === 'unit4.com') return 'unit4';
  if (domain === 'blackboard.com') return 'blackboard';
  if (domain === 'constructor.tech') return 'constructor_tech';
  if (domain === 'terminalfour.com') return 'terminalfour';
  // [2026-10-10] VeryPC's corporate domain is HYPHENATED — very-pc.co.uk, not verypc.co.uk.
  if (domain === 'very-pc.co.uk') return 'verypc';
  if (domain === 'efekta.com') return 'efekta';
  if (domain === 'jpik.com') return 'jpik';
  // [2026-10-18] TestWe — mirrors api/src/services/tenantResolver.ts ('testwe.eu' -> 'testwe').
  if (domain === 'testwe.eu') return 'testwe';
  // [2026-10-21] DreamApply and 4Science — mirror api/src/services/tenantResolver.ts.
  if (domain === 'dreamapply.com') return 'dreamapply';
  if (domain === '4science.com') return '4science';
  // [2026-10-22] Blackbullion — mirrors api/src/services/tenantResolver.ts
  // ('blackbullion.com' -> 'blackbullion'). [2026-10-29] Now GATED like testwe: see
  // canAccessBlackbullionTenant.
  if (domain === 'blackbullion.com') return 'blackbullion';
  // [2026-10-31] Wooclap — mirrors api/src/services/tenantResolver.ts ('wooclap.com' -> 'wooclap').
  // GATED like testwe and blackbullion: see canAccessWooclapTenant.
  if (domain === 'wooclap.com') return 'wooclap';
  // [2026-11-02] Poll Everywhere — mirrors api/src/services/tenantResolver.ts ('polleverywhere.com' -> 'polleverywhere').
  // GATED like wooclap: see canAccessPolleverywhereTenant.
  if (domain === 'polleverywhere.com') return 'polleverywhere';
  // [2026-11-03] Positivo Tecnologia — mirrors api/src/services/tenantResolver.ts ('positivo.com.br' -> 'positivo').
  // GATED like polleverywhere: see canAccessPositivoTenant.
  if (domain === 'positivo.com.br') return 'positivo';
  // Corporate employees use V2 only. Legacy `feedbackfruits` is not a workspace for them.
  if (domain === 'feedbackfruits.com') return 'feedbackfruits_V2';
  if (domain === 'turktelekom.com.tr' || domain === 'argela.com.tr' || domain === 'netsia.com') return 'seba';
  return defaultTenant;
}

/** @skillwell.com users get instructure tenant, not skillwell. */
export function isSkillwellComEmail(email) {
  if (!email || typeof email !== 'string') return false;
  return email.trim().toLowerCase().endsWith('@skillwell.com');
}

/** Diambra access: @diambra.ai OR @ungaii.co users OR superadmin (role === 'admin'). */
export function canAccessDiambraTenant(user) {
  return isDiambraEmail(user?.email) || user?.role === 'admin';
}

/** Evasys access: only @evasys.de users OR superadmin (role === 'admin'). */
export function canAccessEvasysTenant(user) {
  return isEvasysEmail(user?.email) || user?.role === 'admin';
}

/** Learnwise access: only @learnwise.ai users OR superadmin (role === 'admin'). */
export function canAccessLearnwiseTenant(user) {
  return isLearnwiseEmail(user?.email) || user?.role === 'admin';
}

/** Calmedica access: only @calmedica.com users OR superadmin (role === 'admin'). */
export function canAccessCalmedicaTenant(user) {
  return isCalmedicaEmail(user?.email) || user?.role === 'admin';
}

/** Demarque V2: @demarque.com users OR superadmin (role === 'admin'). */
export function canAccessDemarqueTenant(user) {
  return isDemarqueEmail(user?.email) || user?.role === 'admin';
}

/** Bibliotheca: @bibliotheca.com users OR superadmin (role === 'admin'). */
export function canAccessBibliothecaTenant(user) {
  return isBibliothecaEmail(user?.email) || user?.role === 'admin';
}

/**
 * Niryo: @niryo.com users OR superadmin (role === 'admin'). [2026-10-28] Was ungated (a demo prospect);
 * the server half is 'niryo.com' -> 'niryo' in api/src/services/tenantResolver.ts.
 */
export function canAccessNiryoTenant(user) {
  return isNiryoEmail(user?.email) || user?.role === 'admin';
}

/** Exact legacy id `demarque` — not `demarque_V2`. */
export function isLegacyDemarqueTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'demarque';
}

/**
 * Legacy Demarque is not a workspace for @demarque.com employees.
 * Platform admins with a different email can still open it.
 */
export function canAccessLegacyDemarqueTenant(user) {
  if (isDemarqueEmail(user?.email)) return false;
  return user?.role === 'admin';
}

/** Full Fabric V2: @fullfabric.com users OR superadmin (role === 'admin'). */
export function canAccessFullfabricTenant(user) {
  return isFullfabricEmail(user?.email) || user?.role === 'admin';
}

/** Exact legacy id `fullfabric` — not `fullfabric_V2`. */
export function isLegacyFullfabricTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'fullfabric';
}

/**
 * Legacy `fullfabric` is not a workspace for @fullfabric.com employees — they
 * belong on fullfabric_V2. Platform admins with a different email can still open
 * it to inspect the old data. Mirrors canAccessLegacyDemarqueTenant.
 */
export function canAccessLegacyFullfabricTenant(user) {
  if (isFullfabricEmail(user?.email)) return false;
  return user?.role === 'admin';
}

/** Surpass V2: @surpass.com users OR superadmin (role === 'admin'). */
export function canAccessSurpassTenant(user) {
  return isSurpassEmail(user?.email) || user?.role === 'admin';
}

/** Exact legacy id `surpass` — not `surpass_V2`. */
export function isLegacySurpassTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'surpass';
}

/**
 * Legacy `surpass` is not a workspace for @surpass.com employees — they
 * belong on surpass_V2. Platform admins with a different email can still open
 * it to inspect the old data.
 */
export function canAccessLegacySurpassTenant(user) {
  if (isSurpassEmail(user?.email)) return false;
  return user?.role === 'admin';
}

/** Funidata access: @funidata.fi users OR superadmin (role === 'admin'). */
export function canAccessFunidataTenant(user) {
  return isFunidataEmail(user?.email) || user?.role === 'admin';
}

/** Akari access: @akarisoftware.com users OR superadmin (role === 'admin'). */
export function canAccessAkariTenant(user) {
  return isAkariEmail(user?.email) || user?.role === 'admin';
}

/** Almentor access: @almentor.net users OR superadmin (role === 'admin'). */
export function canAccessAlmentorTenant(user) {
  return isAlmentorEmail(user?.email) || user?.role === 'admin';
}

/** Gravyty access: @gravyty.com users OR superadmin (role === 'admin'). */
export function canAccessGravytyTenant(user) {
  return isGravytyEmail(user?.email) || user?.role === 'admin';
}

/** Infonetica access: @infonetica.net users OR superadmin (role === 'admin'). */
export function canAccessInfoneticaTenant(user) {
  return isInfoneticaEmail(user?.email) || user?.role === 'admin';
}

/** Cayuse access: @cayuse.com users OR superadmin (role === 'admin'). */
export function canAccessCayuseTenant(user) {
  return isCayuseEmail(user?.email) || user?.role === 'admin';
}

/** uPlanner access: @uplanner.com users OR superadmin (role === 'admin'). */
export function canAccessUplannerTenant(user) {
  return isUplannerEmail(user?.email) || user?.role === 'admin';
}

/** Edusign access: @edusign.fr users OR superadmin (role === 'admin'). */
export function canAccessEdusignTenant(user) {
  return isEdusignEmail(user?.email) || user?.role === 'admin';
}

/** Liaison access: @liaisonedu.com users OR superadmin (role === 'admin'). */
export function canAccessLiaisonTenant(user) {
  return isLiaisonEmail(user?.email) || user?.role === 'admin';
}

/** Seba access: @turktelekom.com.tr, @argela.com.tr, or @netsia.com users OR superadmin (role === 'admin'). */
export function canAccessSebaTenant(user) {
  return isSebaEmail(user?.email) || user?.role === 'admin';
}

/** Rumi access: @rumiessay.com users OR superadmin (role === 'admin'). */
export function canAccessRumiTenant(user) {
  return isRumiEmail(user?.email) || user?.role === 'admin';
}

/** StudentCRM access: @student-crm.co.uk users OR superadmin (role === 'admin'). */
export function canAccessStudentcrmTenant(user) {
  return isStudentcrmEmail(user?.email) || user?.role === 'admin';
}

/** Unit4 access: @unit4.com users OR superadmin (role === 'admin'). */
export function canAccessUnit4Tenant(user) {
  return isUnit4Email(user?.email) || user?.role === 'admin';
}

/** Blackboard access: @blackboard.com users OR superadmin (role === 'admin'). */
export function canAccessBlackboardTenant(user) {
  return isBlackboardEmail(user?.email) || user?.role === 'admin';
}

/** Constructor access: @constructor.tech users OR superadmin (role === 'admin'). */
export function canAccessConstructorTenant(user) {
  return isConstructorEmail(user?.email) || user?.role === 'admin';
}

/** Efekta access: @efekta.com users OR superadmin (role === 'admin'). */
export function canAccessEfektaTenant(user) {
  return isEfektaEmail(user?.email) || user?.role === 'admin';
}

/** JPIK access: @jpik.com users OR superadmin (role === 'admin'). */
export function canAccessJpikTenant(user) {
  return isJpikEmail(user?.email) || user?.role === 'admin';
}

/** Blackbullion access: @blackbullion.com users OR superadmin (role === 'admin'). [2026-10-29] */
export function canAccessBlackbullionTenant(user) {
  return isBlackbullionEmail(user?.email) || user?.role === 'admin';
}

/** Wooclap access: @wooclap.com users OR superadmin (role === 'admin'). [2026-10-31] */
export function canAccessWooclapTenant(user) {
  return isWooclapEmail(user?.email) || user?.role === 'admin';
}

/** Positivo access: @positivo.com.br users OR superadmin (role === 'admin'). [2026-11-03] */
export function canAccessPositivoTenant(user) {
  return isPositivoEmail(user?.email) || user?.role === 'admin';
}

/** Poll Everywhere access: @polleverywhere.com users OR superadmin (role === 'admin'). [2026-11-02] */
export function canAccessPolleverywhereTenant(user) {
  return isPolleverywhereEmail(user?.email) || user?.role === 'admin';
}

/** TestWe access: @testwe.eu users OR superadmin (role === 'admin'). */
export function canAccessTestweTenant(user) {
  return isTestweEmail(user?.email) || user?.role === 'admin';
}

/** DreamApply access: @dreamapply.com users OR superadmin (role === 'admin'). */
export function canAccessDreamapplyTenant(user) {
  return isDreamapplyEmail(user?.email) || user?.role === 'admin';
}

/** [2026-10-21] 4Science access: @4science.com users OR superadmin (role === 'admin'). */
export function canAccess4scienceTenant(user) {
  return is4scienceEmail(user?.email) || user?.role === 'admin';
}

/** FeedbackFruits V2: @feedbackfruits.com users OR superadmin (role === 'admin'). */
export function canAccessFeedbackfruitsTenant(user) {
  return isFeedbackfruitsEmail(user?.email) || user?.role === 'admin';
}

/** Exact legacy id `feedbackfruits` — not `feedbackfruits_V2`. */
export function isLegacyFeedbackfruitsTenant(tenantId) {
  return String(tenantId ?? '').trim().toLowerCase() === 'feedbackfruits';
}

/**
 * Legacy FeedbackFruits is not a workspace for @feedbackfruits.com employees.
 * Platform admins with a different email can still open it.
 */
export function canAccessLegacyFeedbackfruitsTenant(user) {
  if (isFeedbackfruitsEmail(user?.email)) return false;
  return user?.role === 'admin';
}

/** Calmedica: account_ids excluded from playbook selection (is_visible=false). */
export const CALMEDICA_EXCLUDED_ACCOUNT_IDS = new Set([
  'france_002_001', 'france_002_002', 'france_002_003', 'france_002_004',
  'france_002_005', 'france_002_006', 'france_002_007', 'france_002_008', 'france_002_009'
]);

/** Mixed-case tenant IDs preserved after lowercase normalize (API/header value). */
const CANONICAL_TENANT_IDS = {
  evasys_v2: 'evasys_V2',
  feedbackfruits_v2: 'feedbackfruits_V2',
  demarque_v2: 'demarque_V2',
  fullfabric_v2: 'fullfabric_V2',
  surpass_v2: 'surpass_V2',
  bibliotheca_v2: 'bibliotheca',
  constructor: 'constructor_tech',
};

/** Lowercase slug so UI/API filters match DB tenant_id (e.g. uplanner vs uPlanner). */
export function normalizeTenantId(id) {
  if (id == null || id === '') return null;
  const s = String(id).trim().toLowerCase();
  if (!s) return null;
  if (Object.prototype.hasOwnProperty.call(CANONICAL_TENANT_IDS, s)) {
    return CANONICAL_TENANT_IDS[s];
  }
  return s;
}

export function tenantIdsEqual(a, b) {
  return normalizeTenantId(a) === normalizeTenantId(b);
}

/**
 * Tenant id for storage, override keys, and comparison to a stored value.
 * Returns the canonical stored casing (e.g. feedbackfruits_V2). Null if the
 * cell is missing/blank — never call toLowerCase on a missing CSV field.
 */
export function tenantIdForStoredKey(raw) {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  return normalizeTenantId(trimmed);
}

/** evasys and evasys_V2 share email access + tenant settings. */
export function isEvasysFamilyTenant(tenantId) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  return n === 'evasys' || n === 'evasys_v2';
}

/** feedbackfruits and feedbackfruits_V2 share email access + tenant settings. */
export function isFeedbackfruitsFamilyTenant(tenantId) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  return n === 'feedbackfruits' || n === 'feedbackfruits_v2';
}

/** demarque and demarque_V2 share email access + tenant settings/categories. */
export function isDemarqueFamilyTenant(tenantId) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  return n === 'demarque' || n === 'demarque_v2';
}

/** fullfabric and fullfabric_V2 share email access + tenant settings. */
export function isFullfabricFamilyTenant(tenantId) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  return n === 'fullfabric' || n === 'fullfabric_v2';
}

/** surpass and surpass_V2 share email access + tenant settings. */
export function isSurpassFamilyTenant(tenantId) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  return n === 'surpass' || n === 'surpass_v2';
}

/** bibliotheca tenant (V2-style workspace; id is `bibliotheca`, not `bibliotheca_V2`). */
export function isBibliothecaFamilyTenant(tenantId) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  return n === 'bibliotheca' || n === 'bibliotheca_v2';
}

/** Account-page layout shared by Demarque V2 and Bibliotheca (content tab, library cards). */
export function isLibraryAccountPageTenant(tenantId) {
  return isDemarqueFamilyTenant(tenantId) || isBibliothecaFamilyTenant(tenantId);
}

/**
 * Brand family gate: `brand` and `brand_V2` (or later `_Vn`) share UI features.
 * Prefer this over a literal `=== 'foo_V2'` when a feature belongs to the brand.
 */
export function isBrandTenant(tenantId, brand) {
  const n = String(tenantId ?? '').trim().toLowerCase();
  const b = String(brand ?? '').trim().toLowerCase();
  if (!n || !b) return false;
  if (n === b) return true;
  return n.startsWith(`${b}_`);
}

/**
 * Admin-safe effective tenant for notification/outbox reads.
 * Only uses admin-active-tenant when user.role === 'admin'; avoids stale bleed for non-admins.
 */
export function getEffectiveTenant(user) {
  const adminActiveTenant =
    user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
  const raw = adminActiveTenant || user?.tenant_id || 'instructure';
  const normalized = normalizeTenantId(raw === 'default' ? 'instructure' : raw) || 'instructure';
  if (isLegacyFeedbackfruitsTenant(normalized) && !canAccessLegacyFeedbackfruitsTenant(user)) {
    return 'feedbackfruits_V2';
  }
  if (isLegacyFullfabricTenant(normalized) && !canAccessLegacyFullfabricTenant(user)) {
    return 'fullfabric_V2';
  }
  if (isLegacySurpassTenant(normalized) && !canAccessLegacySurpassTenant(user)) {
    return 'surpass_V2';
  }
  if (isSurpassEmail(user?.email) && user?.role !== 'admin') {
    return 'surpass_V2';
  }
  if (isLegacyDemarqueTenant(normalized) && !canAccessLegacyDemarqueTenant(user)) {
    return 'demarque_V2';
  }
  if (isDemarqueEmail(user?.email) && user?.role !== 'admin') {
    return 'demarque_V2';
  }
  if (isBibliothecaEmail(user?.email) && user?.role !== 'admin') {
    return 'bibliotheca';
  }
  if (isStudentcrmEmail(user?.email) && user?.role !== 'admin') {
    return 'studentcrm';
  }
  if (isUnit4Email(user?.email) && user?.role !== 'admin') {
    return 'unit4';
  }
  if (isBlackboardEmail(user?.email) && user?.role !== 'admin') {
    return 'blackboard';
  }
  if (isConstructorEmail(user?.email) && user?.role !== 'admin') {
    return 'constructor_tech';
  }
  if (isEfektaEmail(user?.email) && user?.role !== 'admin') {
    return 'efekta';
  }
  if (isJpikEmail(user?.email) && user?.role !== 'admin') {
    return 'jpik';
  }
  if (isTestweEmail(user?.email) && user?.role !== 'admin') {
    return 'testwe';
  }
  if (isBlackbullionEmail(user?.email) && user?.role !== 'admin') {
    return 'blackbullion';
  }
  if (isWooclapEmail(user?.email) && user?.role !== 'admin') {
    return 'wooclap';
  }
  if (isPolleverywhereEmail(user?.email) && user?.role !== 'admin') {
    return 'polleverywhere';
  }
  if (isPositivoEmail(user?.email) && user?.role !== 'admin') {
    return 'positivo';
  }
  if (isDreamapplyEmail(user?.email) && user?.role !== 'admin') {
    return 'dreamapply';
  }
  return normalized;
}

/** Returns true if account should be excluded from playbook selection for the given tenant. */
export function isAccountExcludedFromPlaybooks(tenantId, account) {
  if (tenantId !== 'calmedica') return false;
  const id = account?.account_id || account?.id;
  return id && CALMEDICA_EXCLUDED_ACCOUNT_IDS.has(String(id));
}

/**
 * Resolve effective tenant for data fetching / UI.
 * Session/backend is canonical for tenant resolution.
 * Frontend should not infer tenant from email domain here.
 */
export function resolveEffectiveTenant(adminActive, currentTenant, user, defaultTenant = 'instructure') {
  const defNorm = normalizeTenantId(defaultTenant) || 'instructure';
  let raw = adminActive || currentTenant || user?.tenant_id || defaultTenant;
  // Treat 'default' as invalid — free signups should use instructure
  if (!raw || raw === 'default') raw = defaultTenant;
  const effective = normalizeTenantId(raw) || defNorm;

  const fallbackTenant = () => normalizeTenantId(user?.tenant_id) || defNorm;

  if (effective === 'diambra' && !canAccessDiambraTenant(user)) {
    return fallbackTenant();
  }
  if (isEvasysFamilyTenant(effective) && !canAccessEvasysTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'learnwise' && !canAccessLearnwiseTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'calmedica' && !canAccessCalmedicaTenant(user)) {
    return fallbackTenant();
  }
  if (isDemarqueFamilyTenant(effective) && !canAccessDemarqueTenant(user)) {
    if (!canAccessLegacyDemarqueTenant(user)) {
      return 'demarque_V2';
    }
    return defNorm;
  }
  if (isBibliothecaFamilyTenant(effective) && !canAccessBibliothecaTenant(user)) {
    return fallbackTenant();
  }
  if (isLegacyDemarqueTenant(effective) && !canAccessLegacyDemarqueTenant(user)) {
    return 'demarque_V2';
  }
  if (isDemarqueEmail(user?.email) && user?.role !== 'admin') {
    return 'demarque_V2';
  }
  if (isBibliothecaEmail(user?.email) && user?.role !== 'admin') {
    return 'bibliotheca';
  }
  if (isStudentcrmEmail(user?.email) && user?.role !== 'admin') {
    return 'studentcrm';
  }
  if (isUnit4Email(user?.email) && user?.role !== 'admin') {
    return 'unit4';
  }
  if (isBlackboardEmail(user?.email) && user?.role !== 'admin') {
    return 'blackboard';
  }
  if (isConstructorEmail(user?.email) && user?.role !== 'admin') {
    return 'constructor_tech';
  }
  if (isEfektaEmail(user?.email) && user?.role !== 'admin') {
    return 'efekta';
  }
  if (isJpikEmail(user?.email) && user?.role !== 'admin') {
    return 'jpik';
  }
  if (isTestweEmail(user?.email) && user?.role !== 'admin') {
    return 'testwe';
  }
  if (isBlackbullionEmail(user?.email) && user?.role !== 'admin') {
    return 'blackbullion';
  }
  if (isWooclapEmail(user?.email) && user?.role !== 'admin') {
    return 'wooclap';
  }
  if (isPolleverywhereEmail(user?.email) && user?.role !== 'admin') {
    return 'polleverywhere';
  }
  if (isPositivoEmail(user?.email) && user?.role !== 'admin') {
    return 'positivo';
  }
  if (effective === 'funidata' && !canAccessFunidataTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'akari' && !canAccessAkariTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'almentor' && !canAccessAlmentorTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'gravyty' && !canAccessGravytyTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'infonetica' && !canAccessInfoneticaTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'cayuse' && !canAccessCayuseTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'uplanner' && !canAccessUplannerTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'edusign' && !canAccessEdusignTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'liaison' && !canAccessLiaisonTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'seba' && !canAccessSebaTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'rumi' && !canAccessRumiTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'studentcrm' && !canAccessStudentcrmTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'unit4' && !canAccessUnit4Tenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'constructor_tech' && !canAccessConstructorTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'efekta' && !canAccessEfektaTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'jpik' && !canAccessJpikTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'testwe' && !canAccessTestweTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'blackbullion' && !canAccessBlackbullionTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'wooclap' && !canAccessWooclapTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'polleverywhere' && !canAccessPolleverywhereTenant(user)) {
    return fallbackTenant();
  }
  if (effective === 'positivo' && !canAccessPositivoTenant(user)) {
    return fallbackTenant();
  }
  if (isFeedbackfruitsFamilyTenant(effective) && !canAccessFeedbackfruitsTenant(user)) {
    const fallback = fallbackTenant();
    if (isLegacyFeedbackfruitsTenant(fallback) && !canAccessLegacyFeedbackfruitsTenant(user)) {
      return 'feedbackfruits_V2';
    }
    return fallback;
  }
  if (isLegacyFeedbackfruitsTenant(effective) && !canAccessLegacyFeedbackfruitsTenant(user)) {
    return 'feedbackfruits_V2';
  }
  if (isFullfabricFamilyTenant(effective) && !canAccessFullfabricTenant(user)) {
    const fallback = fallbackTenant();
    if (isLegacyFullfabricTenant(fallback) && !canAccessLegacyFullfabricTenant(user)) {
      return 'fullfabric_V2';
    }
    return fallback;
  }
  if (isLegacyFullfabricTenant(effective) && !canAccessLegacyFullfabricTenant(user)) {
    return 'fullfabric_V2';
  }
  if (isSurpassFamilyTenant(effective) && !canAccessSurpassTenant(user)) {
    const fallback = fallbackTenant();
    if (isLegacySurpassTenant(fallback) && !canAccessLegacySurpassTenant(user)) {
      return 'surpass_V2';
    }
    return fallback;
  }
  if (isLegacySurpassTenant(effective) && !canAccessLegacySurpassTenant(user)) {
    return 'surpass_V2';
  }
  if (isSurpassEmail(user?.email) && user?.role !== 'admin') {
    return 'surpass_V2';
  }
  return effective;
}
